import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, ArrowLeft, ArrowRight, AlertTriangle } from 'lucide-react';
import { useCompanies } from '@/hooks/useCompanies';
import { toast } from '@/hooks/use-toast';
import { ColumnMapper } from '@/components/ui/column-mapper';
import {
  ACCOUNT_DB_FIELDS,
  generateSmartMappings,
  transformDataWithMapping,
  validateMappings,
  fetchCompanySchema,
  fetchCustomFields,
  mergeFieldLists,
  type ColumnMapping,
  type DatabaseField,
} from '@/lib/column-mapping-utils';

interface AccountUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: number | string;
  clientName?: string;
  onUploadComplete: () => void;
}

type Step = 'upload' | 'mapping' | 'preview';

const MAX_ROWS = 50000;

export function AccountUploadDialog({ open, onOpenChange, clientId, clientName, onUploadComplete }: AccountUploadDialogProps) {
  // Step state
  const [currentStep, setCurrentStep] = useState<Step>('upload');

  // Upload step state
  const [file, setFile] = useState<File | null>(null);

  // Mapping step state
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } | null>(null);

  // Preview step state
  const [transformedAccounts, setTransformedAccounts] = useState<Record<string, any>[]>([]);
  const [skippedRows, setSkippedRows] = useState(0);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<{
    total: number;
    created: number;
    matched: number;
    errors: number;
  } | null>(null);

  // Errors
  const [errors, setErrors] = useState<string[]>([]);

  // All DB fields (fetched dynamically from companies table schema + custom fields)
  const [customFields, setCustomFields] = useState<DatabaseField[]>([]);
  const allDbFields = useMemo(() => mergeFieldLists(customFields), [customFields]);

  const { uploadMappedAccounts } = useCompanies();

  // Load company schema (standard + custom fields) when dialog opens
  useEffect(() => {
    if (open) {
      fetchCompanySchema().then(setCustomFields).catch(console.error);
    }
  }, [open]);

  // ============================================
  // STEP 1: FILE UPLOAD
  // ============================================

  const downloadTemplate = () => {
    const headers = allDbFields.map(f => f.name);
    const sampleRow = [
      'Acme Corp', 'acme.com', 'linkedin.com/company/acme', 'https://acme.com',
      'Technology', '100-500', '5000000',
      'San Francisco', 'CA', 'USA', '94105', '123 Main St',
      'Leading software company', '+1-555-0123', '', '2015', 'Shopify',
      '', '', '', '', '',
      '5000000', '100000',
      'Tier 1', '85', 'John Smith', 'active', 'website',
      'false', 'true', 'Great prospect', 'saas,enterprise',
    ];

    const csvContent = headers.join(',') + '\n' + sampleRow.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'accounts_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) return;

    setFile(droppedFile);
    setErrors([]);
    parseCSVFile(droppedFile);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    multiple: false
  });

  /** Parse CSV file → extract headers + rawData, generate smart mappings, go to step 2 */
  const parseCSVFile = async (csvFile: File) => {
    try {
      const text = await csvFile.text();
      const lines = text.split('\n');

      if (lines.length < 2) {
        setErrors(['File must contain at least a header row and one data row']);
        return;
      }

      // Parse header row
      const headers = parseCSVLine(lines[0]).map(h => h.trim());
      if (headers.length === 0 || headers.every(h => !h)) {
        setErrors(['Could not detect CSV column headers']);
        return;
      }

      // Parse data rows
      const data: string[][] = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        data.push(parseCSVLine(line));
      }

      if (data.length === 0) {
        setErrors(['No data rows found in CSV']);
        return;
      }

      if (data.length > MAX_ROWS) {
        setErrors([`CSV contains ${data.length.toLocaleString()} rows. Maximum is ${MAX_ROWS.toLocaleString()} per upload. Please split your file.`]);
        return;
      }

      setCsvHeaders(headers);
      setCsvData(data);

      // Ensure company schema is loaded BEFORE generating mappings
      // (fixes race condition where mappings ran before async fetch completed)
      let fieldsForMapping = allDbFields;
      if (customFields.length === 0) {
        try {
          const freshFields = await fetchCompanySchema();
          setCustomFields(freshFields);
          fieldsForMapping = mergeFieldLists(freshFields);
        } catch (e) {
          console.warn('Could not load company schema for mapping:', e);
        }
      }

      // Generate auto-mappings with custom fields included
      const autoMappings = generateSmartMappings(headers, fieldsForMapping);
      setMappings(autoMappings);

      // Run initial validation
      const validation = validateMappings(autoMappings, fieldsForMapping);
      setValidationResult(validation);

      // Transition to mapping step
      setCurrentStep('mapping');
    } catch (error) {
      console.error('Error parsing CSV:', error);
      setErrors(['Failed to parse file. Please check the format and try again.']);
    }
  };

  // ============================================
  // STEP 2: COLUMN MAPPING
  // ============================================

  const handleMappingChange = (csvColumn: string, dbField: string | null) => {
    const newMappings = mappings.map(m => {
      if (m.csvColumn === csvColumn) {
        const field = dbField ? allDbFields.find(f => f.name === dbField) : null;
        return {
          ...m,
          dbField,
          isRequired: field?.required || false,
          dataType: field?.type || 'text',
          confidence: 'none' as const, // Manual change = no auto-confidence
        };
      }
      return m;
    });

    setMappings(newMappings);
    setValidationResult(validateMappings(newMappings, allDbFields));
  };

  const proceedToPreview = () => {
    // Re-validate
    const validation = validateMappings(mappings, allDbFields);
    setValidationResult(validation);

    if (!validation.isValid) {
      toast({
        title: "Mapping errors",
        description: validation.errors.join('. '),
        variant: "destructive",
      });
      return;
    }

    // Transform data using mappings
    const transformed = transformDataWithMapping(csvData, mappings, allDbFields);
    const totalDataRows = csvData.length;
    const validRows = transformed.filter(row => row.company_name);
    const skipped = totalDataRows - validRows.length;

    setTransformedAccounts(validRows);
    setSkippedRows(skipped);
    setCurrentStep('preview');
  };

  // ============================================
  // STEP 3: PREVIEW & UPLOAD
  // ============================================

  const handleUpload = async () => {
    if (transformedAccounts.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const tamListName = clientName ? `${clientName} - TAM` : undefined;
      const result = await uploadMappedAccounts(
        transformedAccounts,
        clientId,
        tamListName,
        // Real progress callback from batch processing
        (processed, total) => {
          const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
          setUploadProgress(pct);
        },
      );

      setUploadProgress(100);
      setUploadResult(result);

      onUploadComplete();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // COMMON
  // ============================================

  const resetDialog = () => {
    setFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setMappings([]);
    setValidationResult(null);
    setTransformedAccounts([]);
    setSkippedRows(0);
    setUploading(false);
    setUploadProgress(0);
    setUploadResult(null);
    setErrors([]);
    setCurrentStep('upload');
    onOpenChange(false);
  };

  const goBack = () => {
    if (currentStep === 'mapping') {
      setCurrentStep('upload');
      setMappings([]);
      setValidationResult(null);
    } else if (currentStep === 'preview') {
      setCurrentStep('mapping');
      setTransformedAccounts([]);
      setSkippedRows(0);
      setUploadResult(null);
    }
  };

  // Get preview column headers (mapped fields that have data)
  const previewHeaders = mappings
    .filter(m => m.dbField)
    .map(m => {
      const field = allDbFields.find(f => f.name === m.dbField);
      return { key: m.dbField!, label: field?.label || m.dbField! };
    });

  const previewRows = transformedAccounts.slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle>
              {currentStep === 'upload' && 'Upload Accounts CSV'}
              {currentStep === 'mapping' && 'Map CSV Columns'}
              {currentStep === 'preview' && 'Preview & Upload'}
            </DialogTitle>
            {/* Step indicator */}
            <div className="flex items-center gap-2">
              <StepIndicator step={1} label="Upload" active={currentStep === 'upload'} completed={currentStep !== 'upload'} />
              <span className="text-muted-foreground">→</span>
              <StepIndicator step={2} label="Mapping" active={currentStep === 'mapping'} completed={currentStep === 'preview'} />
              <span className="text-muted-foreground">→</span>
              <StepIndicator step={3} label="Upload" active={currentStep === 'preview'} completed={false} />
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 flex-1 min-h-0 overflow-y-auto pr-1">
          {/* ============================================ */}
          {/* STEP 1: FILE UPLOAD */}
          {/* ============================================ */}
          {currentStep === 'upload' && (
            <>
              {/* Template Download */}
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Upload a CSV file with account data. Column mapping will be auto-detected.
                </p>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>

              {/* File Drop Zone */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                {isDragActive ? (
                  <p className="text-lg">Drop the file here...</p>
                ) : (
                  <div>
                    <p className="text-lg mb-2">Drag and drop a CSV file here, or click to select</p>
                    <p className="text-sm text-muted-foreground">Supports CSV files up to {MAX_ROWS.toLocaleString()} rows</p>
                  </div>
                )}
                {file && (
                  <p className="mt-3 text-sm font-medium text-primary">
                    Selected: {file.name}
                  </p>
                )}
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc ml-4">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Actions */}
              <div className="flex justify-end">
                <Button variant="outline" onClick={resetDialog}>Cancel</Button>
              </div>
            </>
          )}

          {/* ============================================ */}
          {/* STEP 2: COLUMN MAPPING */}
          {/* ============================================ */}
          {currentStep === 'mapping' && (
            <>
              {/* File info bar */}
              <div className="flex items-center gap-3 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                <FileSpreadsheet className="w-4 h-4" />
                <span>{file?.name}</span>
                <span>•</span>
                <span>{csvData.length.toLocaleString()} rows</span>
                <span>•</span>
                <span>{csvHeaders.length} columns</span>
              </div>

              {/* ColumnMapper component */}
              <ColumnMapper
                onFieldCreated={(field, forCsvColumn) => {
                  // Add the new field to our custom fields list
                  setCustomFields(prev => [...prev, field]);
                  // Auto-map the CSV column to the new field
                  handleMappingChange(forCsvColumn, field.name);
                }}
                csvHeaders={csvHeaders}
                csvData={csvData}
                dbFields={allDbFields}
                mappings={mappings}
                onMappingChange={handleMappingChange}
              />

              {/* Validation messages */}
              {validationResult && (
                <>
                  {validationResult.errors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <ul className="list-disc ml-4">
                          {validationResult.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                  {validationResult.warnings.length > 0 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <ul className="list-disc ml-4">
                          {validationResult.warnings.map((warn, i) => (
                            <li key={i}>{warn}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}

              {/* Actions */}
              <div className="flex justify-between">
                <Button variant="outline" onClick={goBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={proceedToPreview}
                  disabled={!validationResult?.isValid}
                >
                  Continue to Preview
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {/* ============================================ */}
          {/* STEP 3: PREVIEW & UPLOAD */}
          {/* ============================================ */}
          {currentStep === 'preview' && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{transformedAccounts.length.toLocaleString()}</p>
                  <p className="text-sm text-green-600">Accounts Ready</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-700">{skippedRows}</p>
                  <p className="text-sm text-yellow-600">Rows Skipped</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{mappings.filter(m => !m.dbField).length}</p>
                  <p className="text-sm text-blue-600">Columns Ignored</p>
                </div>
              </div>

              {skippedRows > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {skippedRows} row{skippedRows > 1 ? 's' : ''} will be skipped because they are missing a company name.
                  </AlertDescription>
                </Alert>
              )}

              {/* Preview table */}
              {previewRows.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Previewing first {previewRows.length} of {transformedAccounts.length.toLocaleString()} accounts
                  </p>
                  <div className="max-h-72 overflow-auto border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {previewHeaders.slice(0, 6).map(h => (
                            <TableHead key={h.key} className="whitespace-nowrap">{h.label}</TableHead>
                          ))}
                          {previewHeaders.length > 6 && (
                            <TableHead className="text-muted-foreground">+{previewHeaders.length - 6} more</TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewRows.map((row, index) => (
                          <TableRow key={index}>
                            {previewHeaders.slice(0, 6).map(h => (
                              <TableCell key={h.key} className="max-w-[200px] truncate">
                                {row[h.key] != null ? String(row[h.key]) : '-'}
                              </TableCell>
                            ))}
                            {previewHeaders.length > 6 && (
                              <TableCell className="text-muted-foreground">...</TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Uploading {transformedAccounts.length.toLocaleString()} accounts through resolver...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} />
                </div>
              )}

              {/* Upload Result */}
              {uploadResult && (
                <Alert>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription>
                    Upload complete! {uploadResult.created} created, {uploadResult.matched} matched
                    {uploadResult.errors > 0 && `, ${uploadResult.errors} errors`}.
                  </AlertDescription>
                </Alert>
              )}

              {/* Actions */}
              <div className="flex justify-between">
                <Button variant="outline" onClick={goBack} disabled={uploading}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Mapping
                </Button>
                {uploadResult ? (
                  <Button onClick={resetDialog}>
                    Done
                  </Button>
                ) : (
                  <Button
                    onClick={handleUpload}
                    disabled={transformedAccounts.length === 0 || uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload {transformedAccounts.length.toLocaleString()} Accounts
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// STEP INDICATOR COMPONENT
// ============================================

function StepIndicator({ step, label, active, completed }: {
  step: number;
  label: string;
  active: boolean;
  completed: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
        active
          ? 'bg-primary text-primary-foreground'
          : completed
            ? 'bg-green-100 text-green-700'
            : 'bg-muted text-muted-foreground'
      }`}>
        {completed ? '✓' : step}
      </div>
      <span className={`text-xs ${active ? 'font-medium' : 'text-muted-foreground'}`}>
        {label}
      </span>
    </div>
  );
}

// ============================================
// CSV PARSING UTILITY
// ============================================

/** Parse a single CSV line handling quoted values with commas */
function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++; // Skip escaped quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
}
